import { useEffect } from "react";
import { ArrowRight, XCircle, BookOpen } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const PAGE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Cómo calcular la rentabilidad de un piso | PisoScore",
  url: "https://pisoscore.es/guia-inversion-inmobiliaria",
  description:
    "Aprende a calcular la rentabilidad de un piso para alquilar: rentabilidad bruta y neta, cash flow, ROI, gastos y financiación. Guía práctica de PisoScore.",
  inLanguage: "es-ES",
  isPartOf: { "@type": "WebSite", name: "PisoScore", url: "https://pisoscore.es/" },
};

const Chapter = ({ number, title, children }) => (
  <section className="mt-14">
    <div className="flex items-baseline gap-4">
      <span className="font-mono text-sm font-bold text-emerald-600">{number}</span>
      <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{title}</h2>
    </div>
    <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600">{children}</div>
  </section>
);

const Formula = ({ children }) => (
  <div className="my-6 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-6 py-5 text-center">
    <p className="font-mono text-sm font-semibold text-emerald-900 sm:text-base">{children}</p>
  </div>
);

export default function GuiaInversion() {
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="guide-page" className="bg-white">
      <Seo
        title="Cómo calcular la rentabilidad de un piso | PisoScore"
        description="Aprende a calcular la rentabilidad de un piso para alquilar: rentabilidad bruta y neta, cash flow, ROI, gastos y financiación. Guía práctica de PisoScore."
        canonical="https://pisoscore.es/guia-inversion-inmobiliaria"
        ogType="article"
        jsonLd={PAGE_JSONLD}
      />

      <div className="border-b border-slate-100 bg-slate-50/70">
        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-20">
          <Reveal>
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">
              <BookOpen className="h-4 w-4" /> Guía de inversión inmobiliaria
            </p>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Cómo calcular la rentabilidad de un piso para alquilar
            </h1>
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
              Si estás pensando en comprar una vivienda para alquilarla, mirar únicamente el precio de compra y el
              alquiler mensual no es suficiente para decidir si la operación merece la pena. Entre ambos números hay
              impuestos, gastos recurrentes, financiación y periodos sin inquilino que pueden cambiar por completo el
              resultado. En esta guía de{" "}
              <a href="/" className="font-medium text-emerald-600 underline decoration-emerald-300 underline-offset-2 hover:text-emerald-700">
                PisoScore
              </a>{" "}
              aprenderás, paso a paso y con ejemplos, a calcular la rentabilidad real de un piso en España.
            </p>
          </Reveal>
        </div>
      </div>

      <Reveal className="mx-auto max-w-3xl px-4 pb-8 pt-2 sm:px-6">
        <Chapter number="01" title="Qué significa la rentabilidad de un piso">
          <p>
            La rentabilidad de un piso mide qué retorno genera el dinero que has invertido en él. Dicho de forma
            sencilla: de cada 100 € que pones en la operación, ¿cuántos euros vuelven a tu bolsillo cada año?
          </p>
          <p>
            El problema es que «la rentabilidad» no es una sola cifra. La rentabilidad bruta, la rentabilidad neta, el
            cash flow y el retorno sobre el dinero propio miden cosas distintas, y confundirlas lleva a decisiones
            equivocadas. Un piso puede tener una rentabilidad bruta atractiva y, sin embargo, dejarte cada mes menos
            dinero del que esperabas una vez pagados los gastos y la hipoteca. Por eso conviene entender cada métrica
            y utilizarlas juntas, no de forma aislada.
          </p>
        </Chapter>

        <Chapter number="02" title="Cómo calcular la rentabilidad bruta">
          <p>
            La rentabilidad bruta es el cálculo más rápido y el que casi todo el mundo hace mentalmente cuando ve un
            anuncio:
          </p>
          <Formula>Rentabilidad bruta (%) = alquiler anual ÷ precio de compra × 100</Formula>
          <p>Un ejemplo sencillo:</p>
          <ul className="list-disc space-y-1.5 pl-6">
            <li>Precio del piso: 200.000 €</li>
            <li>Alquiler: 1.000 € al mes</li>
            <li>Ingresos anuales por alquiler: 12.000 €</li>
            <li>
              Rentabilidad bruta: 12.000 ÷ 200.000 × 100 = <strong className="font-semibold text-slate-900">6 %</strong>
            </li>
          </ul>
          <p>
            Ese 6 % sirve como primer filtro para comparar zonas o inmuebles entre sí, pero por sí solo no permite
            saber la rentabilidad real de la inversión. No incluye ni los gastos de la compra, ni los gastos de
            mantener el piso, ni el efecto de la financiación. Dos pisos con la misma rentabilidad bruta pueden dar
            resultados muy diferentes cuando se mira el detalle.
          </p>
        </Chapter>

        <Chapter number="03" title="Cómo calcular la rentabilidad neta">
          <p>
            La rentabilidad neta descuenta los gastos recurrentes que tiene cualquier vivienda alquilada. Entre los
            habituales están la comunidad de vecinos, el IBI, el seguro del hogar, el mantenimiento y las pequeñas
            reparaciones, los posibles periodos sin inquilino y, si la externalizas, la gestión del alquiler.
          </p>
          <Formula>
            Rentabilidad neta (%) = (alquiler anual − gastos anuales) ÷ coste total de la inversión × 100
          </Formula>
          <p>
            Fíjate en el denominador: lo razonable es dividir entre el coste total de la inversión (precio más
            impuestos, gastos de compra y reforma), no solo entre el precio del anuncio. Siguiendo con el ejemplo
            anterior, si el piso tiene unos gastos recurrentes de 2.400 € al año, el rendimiento neto del alquiler sería
            de 9.600 € anuales, claramente por debajo de los 12.000 € brutos.
          </p>
          <p>
            Cada vivienda y cada propietario tienen gastos distintos, y el tratamiento fiscal de esos gastos depende de
            la normativa aplicable y de tu situación personal, por lo que conviene confirmarlo con un asesor fiscal
            antes de dar cifras por válidas.
          </p>
          <p>
            Como referencia práctica, al preparar tu análisis conviene listar cada gasto por separado en lugar de
            estimar un porcentaje a ojo: cuota de comunidad, IBI, seguro, una pequeña provisión anual para
            reparaciones y, si quieres ser prudente, un mes de vacancia cada cierto tiempo. Sumar esos conceptos con
            números reales del inmueble concreto es lo que convierte una rentabilidad «de anuncio» en una
            rentabilidad neta creíble.
          </p>
        </Chapter>

        <Chapter number="04" title="Gastos de compra que no debes olvidar">
          <p>
            El precio que aparece en el portal inmobiliario nunca es el desembolso final. Al comprar un piso para
            alquilar, el coste real de adquisición suele incluir impuestos ligados a la transmisión (como el ITP en la
            vivienda usada o el IVA en obra nueva), los aranceles de notaría y registro, los gastos de gestoría y, en
            muchos casos, una reforma o puesta a punto antes de alquilar.
          </p>
          <p>
            Estos costes no son universales: la fiscalidad de la compra depende de la comunidad autónoma, del tipo de
            inmueble y de las circunstancias concretas de la operación y del comprador. Por eso cualquier análisis
            serio debe calcularlos caso por caso en lugar de asumir un porcentaje genérico. Estamos preparando una guía
            dedicada en profundidad a los gastos de compra de una vivienda; mientras tanto, la calculadora de PisoScore
            aplica automáticamente el ITP general de cada comunidad autónoma y te permite ajustar notaría, registro y
            reforma a tu caso.
          </p>
        </Chapter>

        <Chapter number="05" title="Qué es el cash flow inmobiliario">
          <p>El cash flow inmobiliario es el dinero que te queda (o te falta) cada mes:</p>
          <Formula>Cash flow = ingresos del alquiler − gastos − pagos de financiación</Formula>
          <p>
            Es distinto de la rentabilidad. La rentabilidad es un porcentaje anual sobre el capital invertido; el cash
            flow es liquidez real mes a mes. Un piso puede tener una rentabilidad neta razonable y, a la vez, un cash
            flow negativo si la cuota de la hipoteca es alta, lo que te obligaría a poner dinero de tu bolsillo cada
            mes. Al revés, un cash flow positivo no garantiza por sí solo una buena rentabilidad si el dinero propio
            invertido fue muy grande. Son preguntas diferentes: «¿cuánto rinde mi capital?» frente a «¿cuánto dinero me
            queda cada mes?».
          </p>
          <p>
            Un ejemplo rápido: con un alquiler de 1.000 €, unos gastos mensuales de 200 € y una cuota hipotecaria de
            850 €, el cash flow sería de −50 € al mes. La operación podría seguir teniendo sentido a largo plazo por
            la amortización del préstamo o una posible revalorización, pero mes a mes te exigiría aportar dinero, y
            eso conviene saberlo antes de firmar, no después.
          </p>
        </Chapter>

        <Chapter number="06" title="Qué es el ROI y el cash-on-cash">
          <p>
            El ROI (retorno de la inversión) mide el beneficio obtenido en relación con lo invertido, y en inmuebles
            suele analizarse en un horizonte de varios años, incluyendo los flujos de alquiler y la posible
            revalorización.
          </p>
          <h3 className="pt-2 font-display text-lg font-bold text-slate-900">El cash-on-cash: la métrica del dinero propio</h3>
          <p>
            Cuando compras con hipoteca, apenas pones una parte del precio. El cash-on-cash mide precisamente eso:
            qué retorno anual produce el dinero propio que has desembolsado (entrada, impuestos, gastos y reforma)
            después de pagar todos los gastos y la cuota.
          </p>
          <Formula>Cash-on-cash (%) = cash flow anual ÷ dinero propio invertido × 100</Formula>
          <p>
            La financiación puede amplificar el retorno sobre tu capital, pero también amplifica el riesgo: una cuota
            alta reduce el colchón ante imprevistos. Por eso ninguna métrica única es suficiente para decidir; la
            lectura correcta combina rentabilidad neta, cash flow y retorno sobre el dinero propio.
          </p>
        </Chapter>

        <Chapter number="07" title="Ejemplo completo de análisis de un piso">
          <p>
            Veámoslo todo junto con un ejemplo ilustrativo. Las cantidades son hipotéticas y solo sirven para entender
            la mecánica del cálculo: no son una recomendación financiera ni representan costes universales.
          </p>
          <div className="my-6 overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80 text-left">
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Concepto</th>
                  <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Importe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr><td className="px-4 py-2.5 text-slate-600">Precio de compra</td><td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">200.000 €</td></tr>
                <tr><td className="px-4 py-2.5 text-slate-600">Impuestos y gastos de compra (10 %)</td><td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">20.000 €</td></tr>
                <tr><td className="px-4 py-2.5 text-slate-600">Reforma y puesta a punto</td><td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">10.000 €</td></tr>
                <tr><td className="px-4 py-2.5 font-medium text-slate-900">Coste total de la inversión</td><td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900">230.000 €</td></tr>
                <tr><td className="px-4 py-2.5 text-slate-600">Alquiler mensual estimado</td><td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">1.000 €</td></tr>
                <tr><td className="px-4 py-2.5 text-slate-600">Gastos recurrentes anuales</td><td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">2.400 €</td></tr>
                <tr><td className="px-4 py-2.5 text-slate-600">Financiación: 80 % a 30 años (cuota aprox.)</td><td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">696 €/mes</td></tr>
                <tr><td className="px-4 py-2.5 text-slate-600">Dinero propio desembolsado (entrada + gastos)</td><td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">70.000 €</td></tr>
              </tbody>
            </table>
          </div>
          <p>Con estos números, el análisis quedaría así:</p>
          <ul className="list-disc space-y-1.5 pl-6">
            <li>Rentabilidad bruta sobre el coste total: 12.000 ÷ 230.000 = <strong className="font-semibold text-slate-900">5,2 %</strong></li>
            <li>Rentabilidad neta: 9.600 ÷ 230.000 = <strong className="font-semibold text-slate-900">4,2 %</strong></li>
            <li>Cash flow mensual: 1.000 − 200 − 696 = <strong className="font-semibold text-slate-900">+104 €/mes</strong></li>
            <li>Cash-on-cash: 1.248 ÷ 70.000 = <strong className="font-semibold text-slate-900">1,8 %</strong></li>
          </ul>
          <p>
            Observa cómo cada métrica cuenta una parte distinta de la historia: la rentabilidad neta describe el activo,
            mientras que el cash-on-cash describe lo que rinde tu dinero propio con esa financiación concreta. Cambiar
            el porcentaje de hipoteca o el plazo modificaría las dos últimas cifras sin tocar las dos primeras.
          </p>
        </Chapter>

        <Chapter number="08" title="Qué rentabilidad debe tener un piso para ser una buena inversión">
          <p>
            No existe una cifra mágica a partir de la cual cualquier piso sea automáticamente una buena inversión, y
            conviene desconfiar de quien la ofrece. La rentabilidad exigible depende, entre otros factores, del precio
            y la ubicación del inmueble, del riesgo que asumes (estado del edificio, demanda de alquiler de la zona,
            posible vacancia), de la financiación utilizada, de la liquidez que necesitas, de tus expectativas como
            inversor y de las alternativas disponibles para ese mismo dinero.
          </p>
          <p>
            Un 4 % neto en una zona consolidada con alta demanda puede ser una decisión sensata para un perfil
            conservador, mientras que otro inversor podría exigir más retorno a cambio de reformas, más riesgo o menos
            liquidez. La pregunta correcta no es «¿supera el X %?», sino «¿me compensa este retorno frente al riesgo y
            frente a mis alternativas?».
          </p>
          <p>
            También influye el contexto: el tipo de interés al que te financias, la estabilidad de tus ingresos y tu
            horizonte temporal cambian lo que es razonable exigir a cada operación. Por eso dos inversores pueden
            analizar el mismo piso y llegar a conclusiones opuestas sin que ninguno de los dos esté equivocado.
          </p>
        </Chapter>

        <Chapter number="09" title="Errores habituales al calcular la rentabilidad">
          <p>Estos son los fallos que más a menudo distorsionan el análisis de una inversión inmobiliaria:</p>
          <ul className="space-y-3">
            {[
              "Usar únicamente la rentabilidad bruta para decidir, sin descontar ningún gasto.",
              "Olvidar los gastos de compra: impuestos, notaría, registro y reforma forman parte del capital invertido.",
              "Ignorar los gastos recurrentes, como la comunidad, el IBI, el seguro o el mantenimiento.",
              "Asumir una ocupación del 100 %, como si el piso nunca fuera a quedarse sin inquilino ni un mes.",
              "Confundir un cash flow positivo con una alta rentabilidad sobre el dinero propio invertido.",
              "No analizar el efecto de la financiación: la cuota puede convertir una operación razonable en un desembolso mensual.",
            ].map((err, i) => (
              <li key={i} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-3">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
                <span>{err}</span>
              </li>
            ))}
          </ul>
          <p>
            La mayoría de estos errores se evitan con un hábito sencillo: anotar todos los números de la operación
            antes de decidir, incluidos los que no aparecen en el anuncio. Si una cifra es una estimación, márcala
            como tal y comprueba cómo cambia el resultado cuando la mueves un poco arriba o abajo; si la operación
            solo funciona con el escenario más optimista, ya tienes una respuesta.
          </p>
        </Chapter>

        <section className="mt-14">
          <div className="relative overflow-hidden rounded-[2rem] bg-slate-900 px-6 py-12 text-center sm:px-12">
            <div className="absolute -top-20 left-1/2 h-[280px] w-[480px] -translate-x-1/2 rounded-full bg-emerald-500/20 blur-[100px]" aria-hidden="true" />
            <div className="relative">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                Calcula la rentabilidad con PisoScore
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-400">
                ¿Quieres evitar hacer todos estos cálculos a mano? Analiza un piso con PisoScore y compara la
                rentabilidad bruta y neta, el cash flow, la financiación y otros indicadores de la operación en menos de
                2 minutos.
              </p>
              <a
                data-testid="guide-cta-btn"
                href="/#analisis"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-8 py-4 text-sm font-semibold text-white shadow-xl shadow-emerald-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-400"
              >
                Analizar un piso gratis
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <p className="mt-4 text-xs text-slate-500">
                Contenido informativo. No constituye asesoramiento financiero, fiscal ni de inversión personalizado.
              </p>
            </div>
          </div>
        </section>
      </Reveal>
    </main>
  );
}
